export const normalizeCsvFieldName = (header) =>
  String(header).split('\n')[0].trim().replace(/^\uFEFF/, '').toLowerCase();

export const normalizeCsvRow = (row) => {
  const normalized = {};

  for (const [key, value] of Object.entries(row)) {
    const fieldName = normalizeCsvFieldName(key);
    if (!fieldName) {
      continue;
    }
    normalized[fieldName] = typeof value === 'string' ? value.trim() : value;
  }

  return normalized;
};

export const isEmptyCsvRow = (row) =>
  Object.values(row).every((value) => value == null || String(value).trim() === '');

export const normalizeCsvRows = (rows) =>
  rows.map(normalizeCsvRow).filter((row) => !isEmptyCsvRow(row));

const CSV_REQUIRED_FIELDS = [
  'isrc',
  'title',
  'artist_id',
  'track_type',
  'url',
  'writer_1',
  'writer_1_split',
  'lyrics',
  'composition_type',
  'genre',
  'song_key',
  'bpm',
  'duration',
  'mood_1',
  'tags',
  'producer',
  'engineer',
  'instrument_detail_credits',
  'instruments',
];

const CSV_FIELD_LABELS = {
  isrc: 'ISRC',
  title: 'Title',
  artist_id: 'Artist ID',
  track_type: 'Track type',
  url: 'URL',
  writer_1: 'Writer 1',
  writer_1_split: 'Writer 1 split',
  writer_2_split: 'Writer 2 split',
  writer_3_split: 'Writer 3 split',
  writer_4_split: 'Writer 4 split',
  writer_5_split: 'Writer 5 split',
  lyrics: 'Lyrics',
  lyrics_type: 'Lyrics type',
  composition_type: 'Composition type',
  genre: 'Genre',
  song_key: 'Song key',
  bpm: 'BPM',
  duration: 'Duration',
  mood_1: 'Mood 1',
  tags: 'Tags',
  producer: 'Producer',
  engineer: 'Engineer',
  instrument_detail_credits: 'Instrument detail credits',
  instruments: 'Instruments',
};

const isBlank = (value) => value == null || String(value).trim() === '';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isValidUuid = (value) => UUID_PATTERN.test(String(value).trim());

const validateArtistReference = (
  value,
  fieldLabel,
  rowNumber,
  { knownArtistIds = null } = {}
) => {
  const errors = [];
  const trimmed = String(value ?? '').trim();

  if (isBlank(trimmed)) {
    return errors;
  }

  if (!isValidUuid(trimmed)) {
    errors.push(`Row ${rowNumber}: Invalid ${fieldLabel} "${trimmed}" (must be a valid UUID)`);
    return errors;
  }

  if (knownArtistIds && !knownArtistIds.has(trimmed)) {
    errors.push(`Row ${rowNumber}: ${fieldLabel} "${trimmed}" was not found in the artist library`);
  }

  return errors;
};

const hasLyrics = (value) => {
  const normalized = String(value).trim().toLowerCase();
  return normalized === 'true' || normalized === 'yes' || normalized === '1';
};

export const validateCsvRowFields = (row, rowNumber, options = {}) => {
  const errors = [];

  CSV_REQUIRED_FIELDS.forEach((field) => {
    if (isBlank(row[field])) {
      errors.push(`Row ${rowNumber}: Missing ${CSV_FIELD_LABELS[field] || field}`);
    }
  });

  if (hasLyrics(row.lyrics) && isBlank(row.lyrics_type)) {
    errors.push(`Row ${rowNumber}: Missing ${CSV_FIELD_LABELS.lyrics_type} (required when lyrics is TRUE)`);
  }

  for (let writerIndex = 2; writerIndex <= 5; writerIndex += 1) {
    const writerField = `writer_${writerIndex}`;
    const splitField = `writer_${writerIndex}_split`;
    if (!isBlank(row[writerField]) && isBlank(row[splitField])) {
      errors.push(
        `Row ${rowNumber}: Missing ${CSV_FIELD_LABELS[splitField] || splitField} (required when ${CSV_FIELD_LABELS[writerField] || writerField} is provided)`
      );
    }
  }

  if (!isBlank(row.artist_id)) {
    errors.push(
      ...validateArtistReference(row.artist_id, CSV_FIELD_LABELS.artist_id, rowNumber, {
        knownArtistIds: options.knownArtistIds,
      })
    );
  }

  if (!isBlank(row.featured_artist_id)) {
    errors.push(
      ...validateArtistReference(row.featured_artist_id, 'Featured artist ID', rowNumber, {
        knownArtistIds: options.knownArtistIds,
      })
    );
  }

  return errors;
};

export const validateCsvRows = (rows, options = {}) => {
  const errors = [];

  rows.forEach((row, index) => {
    errors.push(...validateCsvRowFields(row, index + 2, options));
  });

  return errors;
};
